import { getCommercialSimulatorPolicy } from '../config/commercialSimulatorPolicy';
import { formatNumber } from './numberFormatter';

/**
 * Redondeo a 2 decimales, equivalente a PHP round($value, 2)
 * usado por Simulator::Simulate() / CalcInstallmentAmount.
 */
export function roundInstallment(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

/**
 * Cuota francesa con TEM fija + IVA sobre interés.
 * Paridad con FixedTermRateStrategy::CalcInstallmentAmount().
 *
 * TEM = TNA / 12
 * monthlyRate = TEM * (1 + IVA)
 * cuota = (monthlyRate * capital) / (1 - (1 + monthlyRate)^(-n))
 */
export function calculateInstallment({ capital, tna, installments, iva }) {
  const financedCapital = Number(capital);
  const annualRate = Number(tna);
  const terms = Number(installments);
  const vat = Number(iva);

  if (!(financedCapital > 0) || !(annualRate > 0) || !(terms > 0) || !(vat >= 0)) {
    return null;
  }

  const tem = annualRate / 12;
  const monthlyRate = tem * (1 + vat);
  const installment =
    (monthlyRate * financedCapital) / (1 - Math.pow(1 + monthlyRate, -terms));

  return roundInstallment(installment);
}

export function getSimulatorProducts(policy = getCommercialSimulatorPolicy()) {
  if (!policy?.products) {
    return [];
  }

  return Object.entries(policy.products).map(([id, product]) => ({
    id,
    name: product.name,
    maxAmount: product.max_amount,
  }));
}

export function getSimulatorInstallmentOptions(policy = getCommercialSimulatorPolicy()) {
  if (!policy?.installments) {
    return [];
  }

  const options = [];
  for (let value = policy.installments.min; value <= policy.installments.max; value += 1) {
    options.push(value);
  }
  return options;
}

export function validateSimulationInput({
  productId,
  amount,
  installments,
  policy = getCommercialSimulatorPolicy(),
}) {
  const errors = {};
  const product = policy?.products?.[productId];

  if (!product) {
    errors.productId = 'Elegí un producto.';
  }

  if (amount === '' || amount === null || amount === undefined) {
    errors.amount = null;
  } else if (!(Number(amount) > 0)) {
    errors.amount = 'El monto debe ser mayor a 0.';
  } else if (product && Number(amount) > product.max_amount) {
    errors.amount = `El monto no puede superar $${formatNumber(product.max_amount)} para ${product.name}.`;
  }

  const terms = Number(installments);
  const minInstallments = policy?.installments?.min;
  const maxInstallments = policy?.installments?.max;
  if (
    !Number.isInteger(minInstallments) ||
    !Number.isInteger(maxInstallments) ||
    !Number.isInteger(terms) ||
    terms < minInstallments ||
    terms > maxInstallments
  ) {
    errors.installments =
      Number.isInteger(minInstallments) && Number.isInteger(maxInstallments)
        ? `Las cuotas deben estar entre ${minInstallments} y ${maxInstallments}.`
        : 'Elegí un producto.';
  }

  const hasBlockingError = Object.values(errors).some((message) => message);
  const hasAmount = amount !== '' && amount !== null && amount !== undefined;

  return {
    ok: !hasBlockingError && Boolean(product) && hasAmount && Number(amount) > 0,
    errors,
    product,
  };
}

export function quoteModalities({
  productId,
  amount,
  installments,
  policy = getCommercialSimulatorPolicy(),
}) {
  const validation = validateSimulationInput({
    productId,
    amount,
    installments,
    policy,
  });

  if (!validation.ok || !policy?.modalities) {
    return {
      ok: false,
      errors: validation.errors,
      quotes: [],
    };
  }

  const quotes = Object.entries(policy.modalities).map(([id, modality]) => {
    const capital = Number(amount) * modality.capital_percentage;

    return {
      id,
      name: modality.name,
      tna: modality.tna,
      capital,
      installmentAmount: calculateInstallment({
        capital,
        tna: modality.tna,
        installments: Number(installments),
        iva: policy.iva,
      }),
    };
  });

  return {
    ok: true,
    errors: validation.errors,
    quotes,
  };
}
